import { useEffect, useRef } from "react";
import * as THREE from "three";

const DECK_IMAGE = "/images/hlt-submit-decks.png";

function createGlowTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 256;
  const context = canvas.getContext("2d");
  const gradient = context.createRadialGradient(256, 128, 18, 256, 128, 250);

  gradient.addColorStop(0, "rgba(255,223,96,0.28)");
  gradient.addColorStop(0.26, "rgba(34,184,255,0.2)");
  gradient.addColorStop(0.58, "rgba(212,175,55,0.08)");
  gradient.addColorStop(1, "rgba(0,0,0,0)");
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function createDeckMaterial(texture, { opacity = 1, tint = 0, lift = 0.24 } = {}) {
  return new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uMap: { value: texture },
      uOpacity: { value: opacity },
      uTintStrength: { value: tint },
      uLift: { value: lift },
      uPointer: { value: new THREE.Vector2(0, 0) },
      uBlue: { value: new THREE.Color("#22b8ff") },
      uGold: { value: new THREE.Color("#d4af37") },
    },
    vertexShader: `
      uniform float uLift;
      uniform vec2 uPointer;
      varying vec2 vUv;

      void main() {
        vUv = uv;
        vec3 transformed = position;
        float focus = 1.0 - smoothstep(0.0, 0.78, distance(uv, vec2(0.5, 0.54)));
        float platterLeft = 1.0 - smoothstep(0.0, 0.18, distance(uv, vec2(0.29, 0.52)));
        float platterRight = 1.0 - smoothstep(0.0, 0.18, distance(uv, vec2(0.69, 0.52)));
        float uploadButton = 1.0 - smoothstep(0.0, 0.16, distance(uv, vec2(0.49, 0.78)));
        float depth = max(focus * 0.54, max(platterLeft, platterRight) * 0.42);
        depth = max(depth, uploadButton * 0.62);

        transformed.z += depth * (uLift + length(uPointer) * 0.22);
        transformed.x += (uv.x - 0.5) * depth * uPointer.x * 0.28;
        transformed.y -= (uv.y - 0.5) * depth * uPointer.y * 0.18;

        gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D uMap;
      uniform float uOpacity;
      uniform float uTintStrength;
      uniform vec3 uBlue;
      uniform vec3 uGold;
      varying vec2 vUv;

      void main() {
        vec4 tex = texture2D(uMap, vUv);
        float brightness = max(max(tex.r, tex.g), tex.b);
        float darkness = min(min(tex.r, tex.g), tex.b);
        float chroma = brightness - darkness;
        float highlightAlpha = max(
          smoothstep(0.08, 0.22, brightness),
          smoothstep(0.07, 0.2, chroma)
        );
        float leftPlatter = 1.0 - smoothstep(
          0.82,
          1.08,
          length(vec2((vUv.x - 0.29) / 0.2, (vUv.y - 0.45) / 0.2))
        );
        float rightPlatter = 1.0 - smoothstep(
          0.82,
          1.08,
          length(vec2((vUv.x - 0.69) / 0.21, (vUv.y - 0.45) / 0.2))
        );
        float mixerCore = 1.0 - smoothstep(
          0.84,
          1.1,
          length(vec2((vUv.x - 0.5) / 0.16, (vUv.y - 0.47) / 0.31))
        );
        float deckBase = 1.0 - smoothstep(
          0.88,
          1.14,
          length(vec2((vUv.x - 0.49) / 0.4, (vUv.y - 0.34) / 0.18))
        );
        float uploadButton = 1.0 - smoothstep(
          0.0,
          0.19,
          distance(vUv, vec2(0.49, 0.78))
        );
        float objectMask = max(max(max(leftPlatter, rightPlatter), max(mixerCore, deckBase)), uploadButton);
        float rightPanelCut = smoothstep(0.56, 0.66, vUv.x) * smoothstep(0.52, 0.6, vUv.y);
        float farRightPanelCut = smoothstep(0.7, 0.78, vUv.x) * smoothstep(0.26, 0.36, vUv.y);
        float bottomShadowCut = (1.0 - smoothstep(0.0, 0.18, vUv.y));
        float imageAlpha = highlightAlpha * objectMask * (1.0 - rightPanelCut) * (1.0 - farRightPanelCut) * (1.0 - bottomShadowCut);
        float bottomFloatMask = smoothstep(0.16, 0.3, vUv.y);
        float edgeFade = smoothstep(0.0, 0.045, vUv.x)
          * smoothstep(1.0, 0.955, vUv.x)
          * smoothstep(0.0, 0.045, vUv.y)
          * smoothstep(1.0, 0.955, vUv.y);
        vec3 lifted = min(vec3(1.0), tex.rgb * 1.58);
        vec3 cinematic = pow(lifted, vec3(0.82));
        vec3 glowTint = mix(uBlue, uGold, 0.26);
        float glassRim = smoothstep(0.1, 0.42, chroma) * (0.36 + abs(vUv.x - 0.5) * 0.9);
        float goldTrim = smoothstep(0.16, 0.48, tex.r - tex.b * 0.18);
        vec3 glassColor = cinematic + uBlue * glassRim * 0.22 + uGold * goldTrim * 0.12;
        vec3 color = mix(glassColor, glowTint, uTintStrength);
        float alpha = imageAlpha * edgeFade * bottomFloatMask * uOpacity;
        if (alpha < 0.018) discard;

        gl_FragColor = vec4(color, alpha);
      }
    `,
  });
}

export default function SubmissionDeck3D() {
  const shellRef = useRef(null);
  const canvasRef = useRef(null);
  const pointerRef = useRef({ x: 0, y: 0, hovering: false });

  useEffect(() => {
    const shell = shellRef.current;
    const canvas = canvasRef.current;
    if (!shell || !canvas) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
    camera.position.set(0, 0, 5.25);

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      canvas,
      powerPreference: "high-performance",
    });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.8));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;

    const textureLoader = new THREE.TextureLoader();
    const deckTexture = textureLoader.load(DECK_IMAGE);
    deckTexture.colorSpace = THREE.SRGBColorSpace;
    deckTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();

    const glowTexture = createGlowTexture();
    const stage = new THREE.Group();
    const deckGroup = new THREE.Group();
    stage.add(deckGroup);
    scene.add(stage);

    const backGlow = new THREE.Mesh(
      new THREE.PlaneGeometry(5.35, 2.55),
      new THREE.MeshBasicMaterial({
        map: glowTexture,
        opacity: 0.34,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      })
    );
    backGlow.position.set(0.04, -0.02, -0.42);
    deckGroup.add(backGlow);

    const deckGeometry = new THREE.PlaneGeometry(5.75, 3.23, 118, 66);
    const layers = [
      { x: -0.055, y: 0.018, z: -0.12, opacity: 0.08, tint: 0.36, lift: 0.16, scale: 1.014 },
      { x: -0.026, y: 0.01, z: -0.06, opacity: 0.14, tint: 0.18, lift: 0.2, scale: 1.006 },
      { x: 0, y: 0, z: 0, opacity: 1, tint: 0, lift: 0.34, scale: 1 },
    ];
    const materials = [];

    layers.forEach((layer, index) => {
      const material = createDeckMaterial(deckTexture, layer);
      material.renderOrder = index + 1;
      materials.push(material);

      const mesh = new THREE.Mesh(deckGeometry, material);
      mesh.position.set(layer.x, layer.y, layer.z);
      mesh.scale.setScalar(layer.scale);
      mesh.renderOrder = index + 1;
      deckGroup.add(mesh);
    });

    const sparkCount = 58;
    const sparkPositions = new Float32Array(sparkCount * 3);
    for (let index = 0; index < sparkCount; index += 1) {
      const angle = (index / sparkCount) * Math.PI * 2;
      const radius = 1.2 + (index % 9) * 0.16;
      sparkPositions[index * 3] = Math.cos(angle) * radius + (Math.random() - 0.5) * 0.34;
      sparkPositions[index * 3 + 1] = Math.sin(angle * 1.7) * 0.64 + (Math.random() - 0.5) * 0.32;
      sparkPositions[index * 3 + 2] = -0.18 + (Math.random() - 0.5) * 0.56;
    }
    const sparkGeometry = new THREE.BufferGeometry();
    sparkGeometry.setAttribute("position", new THREE.BufferAttribute(sparkPositions, 3));
    const sparks = new THREE.Points(
      sparkGeometry,
      new THREE.PointsMaterial({
        color: "#f5d866",
        opacity: 0.42,
        size: 0.03,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      })
    );
    sparks.position.set(0.04, 0.04, -0.22);
    deckGroup.add(sparks);

    const musicGroup = new THREE.Group();
    musicGroup.position.set(0.2, 0.72, 0.12);
    musicGroup.scale.set(0.88, 0.88, 0.88);
    deckGroup.add(musicGroup);

    const barGeometry = new THREE.BoxGeometry(0.028, 1, 0.028);
    const waveformBands = [
      { count: 46, width: 3.36, color: "#35c7ff", y: 0.02, z: 0.1, phase: 0.2, height: 0.58 },
      { count: 36, width: 2.82, color: "#f6d76f", y: -0.06, z: 0.24, phase: 1.3, height: 0.44 },
      { count: 28, width: 2.16, color: "#35c7ff", y: -0.13, z: 0.38, phase: 2.4, height: 0.34 },
    ].map((config, bandIndex) => {
      const group = new THREE.Group();
      const material = new THREE.MeshBasicMaterial({
        color: config.color,
        opacity: 0,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

      const bars = [];
      for (let barIndex = 0; barIndex < config.count; barIndex += 1) {
        const t = barIndex / (config.count - 1);
        const envelope = Math.sin(Math.PI * t);
        const rhythm =
          Math.sin(t * Math.PI * 8 + config.phase) * 0.5 +
          Math.sin(t * Math.PI * 21 + config.phase * 0.7) * 0.22 +
          0.62;
        const height = (0.12 + Math.abs(rhythm) * config.height * envelope) * (bandIndex === 0 ? 1 : 0.9);
        const bar = new THREE.Mesh(barGeometry, material);

        bar.position.set(
          (t - 0.5) * config.width,
          0,
          Math.sin(t * Math.PI * 3 + config.phase) * 0.04
        );
        bar.scale.set(1, height, 1);
        bar.userData = {
          baseHeight: height,
          phase: config.phase + barIndex * 0.38,
        };
        bar.renderOrder = 8 + bandIndex;
        group.add(bar);
        bars.push(bar);
      }

      group.position.set(bandIndex * 0.12 - 0.08, config.y, config.z);
      group.rotation.z = bandIndex === 1 ? -0.01 : 0.016;
      musicGroup.add(group);
      return { bars, group, material, ...config };
    });

    const pulseRings = [0, 1, 2].map((index) => {
      const material = new THREE.MeshBasicMaterial({
        color: index === 1 ? "#35c7ff" : "#f6d76f",
        opacity: 0,
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.32 + index * 0.13, 0.0045, 8, 96), material);
      ring.position.set(0.02, 0.06, -0.02 + index * 0.06);
      ring.scale.set(1, 0.28, 1);
      ring.renderOrder = 11 + index;
      musicGroup.add(ring);
      return { ring, material, index };
    });

    let frame = 0;
    let targetScale = 1;
    let isCompact = false;
    let isTight = false;
    const targetRotation = { x: 0.02, y: 0.04 };

    const resize = () => {
      const rect = shell.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);
      isCompact = width < 560;
      isTight = width < 390;

      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.position.z = isTight ? 8.05 : isCompact ? 7.32 : 5.62;
      camera.updateProjectionMatrix();
      stage.scale.setScalar(isTight ? 0.78 : isCompact ? 0.9 : 1.03);
      stage.position.set(isCompact ? -0.02 : 0.04, isCompact ? -0.02 : -0.04, 0);
    };

    const onPointerMove = (event) => {
      const rect = shell.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;

      pointerRef.current.x = Math.max(-1, Math.min(1, x));
      pointerRef.current.y = Math.max(-1, Math.min(1, y));
      pointerRef.current.hovering = true;
      targetRotation.y = 0.04 + pointerRef.current.x * 0.34;
      targetRotation.x = 0.02 - pointerRef.current.y * 0.24;
      targetScale = isCompact ? 1.075 : 1.1;
    };

    const onPointerLeave = () => {
      pointerRef.current.x = 0;
      pointerRef.current.y = 0;
      pointerRef.current.hovering = false;
      targetRotation.y = 0.04;
      targetRotation.x = 0.02;
      targetScale = 1;
    };

    const animate = () => {
      frame = window.requestAnimationFrame(animate);
      const time = performance.now() * 0.001;
      const { x, y, hovering } = pointerRef.current;

      deckGroup.rotation.y += (targetRotation.y - deckGroup.rotation.y) * 0.08;
      deckGroup.rotation.x += (targetRotation.x - deckGroup.rotation.x) * 0.08;
      deckGroup.rotation.z += (x * 0.02 - deckGroup.rotation.z) * 0.08;
      deckGroup.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.07);
      deckGroup.position.z += ((hovering ? (isCompact ? 0.22 : 0.48) : 0) - deckGroup.position.z) * 0.08;
      deckGroup.position.y +=
        ((hovering ? (isCompact ? 0.03 : 0.08) : 0) + Math.sin(time * 1.05) * 0.024 - deckGroup.position.y) *
        0.08;
      sparks.rotation.z = time * 0.08;
      sparks.rotation.y = x * 0.08;
      sparks.material.opacity += ((hovering ? 0.34 : 0.08) - sparks.material.opacity) * 0.08;
      backGlow.material.opacity = 0.22 + Math.sin(time * 1.35) * 0.035 + (hovering ? 0.12 : 0);

      musicGroup.position.x += (x * (isCompact ? 0.035 : 0.08) + (isCompact ? 0.1 : 0.2) - musicGroup.position.x) * 0.08;
      musicGroup.position.y +=
        ((hovering ? (isCompact ? 0.58 : 0.78) : isCompact ? 0.5 : 0.66) +
          Math.sin(time * 1.15) * 0.025 -
          musicGroup.position.y) *
        0.08;
      musicGroup.position.z += ((hovering ? (isCompact ? 0.42 : 0.72) : 0.12) - musicGroup.position.z) * 0.08;
      musicGroup.rotation.x += (-y * 0.08 - musicGroup.rotation.x) * 0.08;
      musicGroup.rotation.y += (x * 0.18 - musicGroup.rotation.y) * 0.08;
      const musicScale = hovering ? (isCompact ? 1.08 : 1.08) : isCompact ? 0.88 : 0.88;
      musicGroup.scale.lerp(new THREE.Vector3(musicScale, musicScale, musicScale), 0.07);

      waveformBands.forEach((wave, index) => {
        const hoverOpacity = 0.82 - index * 0.15;
        wave.material.opacity += ((hovering ? hoverOpacity : 0) - wave.material.opacity) * 0.12;
        wave.group.position.y = wave.y + Math.sin(time * (1.3 + index * 0.18) + wave.phase) * 0.03;
        wave.group.position.z = wave.z + Math.sin(time * 1.6 + index) * 0.05;
        wave.group.scale.x += ((hovering ? 1.08 + index * 0.025 : 0.9) - wave.group.scale.x) * 0.08;

        wave.bars.forEach((bar, barIndex) => {
          const pulse = 0.72 + (Math.sin(time * 5.8 + bar.userData.phase + barIndex * 0.08) + 1) * 0.22;
          const targetHeight = bar.userData.baseHeight * (hovering ? pulse + 0.48 : 0.45);
          bar.scale.y += (targetHeight - bar.scale.y) * 0.18;
        });
      });

      pulseRings.forEach(({ ring, material, index }) => {
        const pulse = 1 + Math.sin(time * 1.85 + index * 0.72) * 0.08 + (hovering ? 0.18 + index * 0.06 : 0);
        ring.scale.set(pulse, pulse * 0.28, pulse);
        ring.rotation.z = time * (0.18 + index * 0.04);
        material.opacity += ((hovering ? 0.26 - index * 0.045 : 0) - material.opacity) * 0.1;
      });

      materials.forEach((material) => {
        material.uniforms.uPointer.value.set(x, y);
      });

      camera.position.x += (x * 0.09 - camera.position.x) * 0.06;
      camera.position.y += (-y * 0.05 - camera.position.y) * 0.06;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };

    resize();
    animate();
    shell.addEventListener("pointermove", onPointerMove);
    shell.addEventListener("pointerleave", onPointerLeave);
    window.addEventListener("resize", resize);

    return () => {
      window.cancelAnimationFrame(frame);
      shell.removeEventListener("pointermove", onPointerMove);
      shell.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("resize", resize);
      deckTexture.dispose();
      glowTexture.dispose();
      deckGeometry.dispose();
      sparkGeometry.dispose();
      barGeometry.dispose();
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) object.material.dispose();
      });
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={shellRef}
      className="relative h-[14.75rem] min-h-[14.75rem] overflow-hidden bg-transparent sm:h-[18rem] sm:min-h-[18rem] md:h-[22rem] lg:h-[21rem]"
    >
      <canvas
        ref={canvasRef}
        data-submission-deck-3d
        aria-hidden="true"
        className="absolute inset-0 h-full w-full"
      />
    </div>
  );
}
